import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Network,
  Plus,
  Sparkles,
  Loader,
  ChevronLeft,
  FileText,
  Link2,
  ExternalLink,
  Trash2,
  Wand2,
  ArrowUpRight,
  ArrowDownLeft,
  Check,
  X,
  Eye,
  Send,
} from "lucide-react";
import api from "../api/client";
import ScoreRing from "./ScoreRing";

const STATUS_COLORS = {
  planning: "bg-gray-700 text-gray-300",
  building: "bg-amber-900/30 text-amber-400",
  published: "bg-green-900/30 text-green-400",
  optimizing: "bg-blue-900/30 text-blue-400",
};

const PILLAR_STATUS_COLORS = {
  not_started: "bg-gray-700 text-gray-300",
  generating: "bg-blue-900/30 text-blue-400",
  draft: "bg-amber-900/30 text-amber-400",
  failed: "bg-red-900/30 text-red-400",
  published: "bg-green-900/30 text-green-400",
};

const CONTENT_TYPE_LABELS = {
  service: "Service",
  location: "Location",
  blog: "Blog",
  faq: "FAQ",
  guide: "Guide",
  comparison: "Comparison",
  cost: "Cost Guide",
};

const CONTENT_TYPE_COLORS = {
  service: "bg-blue-900/20 text-blue-400",
  location: "bg-purple-900/20 text-purple-400",
  blog: "bg-green-900/20 text-green-400",
  faq: "bg-amber-900/20 text-amber-400",
  guide: "bg-cyan-900/20 text-cyan-400",
  comparison: "bg-pink-900/20 text-pink-400",
  cost: "bg-copper/20 text-copper",
};

function LinkStatusIcon({ linkedToPillar, pillarLinksHere }) {
  if (linkedToPillar && pillarLinksHere) {
    return <span title="Linked both ways" className="text-green-400 text-xs font-mono">&#x2194;</span>;
  }
  if (linkedToPillar) {
    return <ArrowUpRight size={14} className="text-amber-400" title="Links to pillar only" />;
  }
  if (pillarLinksHere) {
    return <ArrowDownLeft size={14} className="text-amber-400" title="Pillar links here only" />;
  }
  return <X size={14} className="text-gray-600" title="No links" />;
}

// ── Hub and Spoke Diagram ──
function HubSpokeDiagram({ cluster }) {
  const pages = cluster.cluster_pages || [];
  const total = pages.length;
  if (total === 0) return null;

  const cx = 200, cy = 200, pillarR = 40, clusterR = 22, orbitR = 140;
  const svgSize = 400;

  return (
    <div className="flex justify-center">
      <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} className="max-w-full">
        {/* Connection lines */}
        {pages.map((p, i) => {
          const angle = (2 * Math.PI * i) / total - Math.PI / 2;
          const px = cx + orbitR * Math.cos(angle);
          const py = cy + orbitR * Math.sin(angle);
          let strokeColor = "#374151"; // gray - no links
          let strokeDash = "6 4";
          if (p.linked_to_pillar && p.pillar_links_here) {
            strokeColor = "#22c55e"; strokeDash = "none"; // green - both
          } else if (p.linked_to_pillar || p.pillar_links_here) {
            strokeColor = "#eab308"; strokeDash = "none"; // yellow - one way
          }
          return (
            <line key={`line-${i}`} x1={cx} y1={cy} x2={px} y2={py}
              stroke={strokeColor} strokeWidth={2} strokeDasharray={strokeDash} />
          );
        })}

        {/* Pillar node */}
        <circle cx={cx} cy={cy} r={pillarR} fill="#111827" stroke="#b87333" strokeWidth={3} />
        <text x={cx} y={cy - 6} textAnchor="middle" fill="white" fontSize={10} fontWeight="bold">Pillar</text>
        <text x={cx} y={cy + 8} textAnchor="middle" fill="#9ca3af" fontSize={8}>
          {cluster.core_keyword?.substring(0, 15)}{cluster.core_keyword?.length > 15 ? "..." : ""}
        </text>

        {/* Cluster nodes */}
        {pages.map((p, i) => {
          const angle = (2 * Math.PI * i) / total - Math.PI / 2;
          const px = cx + orbitR * Math.cos(angle);
          const py = cy + orbitR * Math.sin(angle);
          const fill = p.status === "published" ? "#166534" : p.status === "draft" ? "#78350f" : "#1f2937";
          const stroke = p.status === "published" ? "#22c55e" : p.status === "draft" ? "#eab308" : "#4b5563";
          return (
            <g key={`node-${i}`}>
              <circle cx={px} cy={py} r={clusterR} fill={fill} stroke={stroke} strokeWidth={2} />
              <text x={px} y={py + 4} textAnchor="middle" fill="white" fontSize={7}>
                {(p.title || "").substring(0, 8)}{p.title?.length > 8 ? ".." : ""}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Create Cluster Modal ──
function CreateClusterModal({ selectedSite, onClose, onCreated }) {
  const [mode, setMode] = useState("keyword"); // keyword | group
  const [keyword, setKeyword] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groups, setGroups] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (selectedSite) {
      api.get(`/websites/${selectedSite.id}/keyword-groups/`)
        .then((res) => setGroups(res.data || []))
        .catch(() => {});
    }
  }, [selectedSite]);

  const handleGenerate = () => {
    setGenerating(true);
    const payload = mode === "keyword" ? { keyword } : { keyword_group: groupName };
    api.post(`/websites/${selectedSite.id}/topic-clusters/generate/`, payload)
      .then((res) => {
        if (res.data.plan) {
          // Synchronous result (shouldn't happen with new backend, but handle gracefully)
          setPlan(res.data.plan);
          setGenerating(false);
        } else if (res.data.plan_key) {
          // Async — poll for result
          const planKey = res.data.plan_key;
          const poll = setInterval(() => {
            api.get(`/websites/${selectedSite.id}/topic-clusters/plan-status/?plan_key=${planKey}`)
              .then((r) => {
                if (r.data.status === "done") {
                  clearInterval(poll);
                  let planData = r.data.plan;
                  // Handle plan_text fallback (AI returned raw text)
                  if (!planData && r.data.plan_text) {
                    try {
                      let raw = r.data.plan_text.trim();
                      if (raw.startsWith("```")) {
                        raw = raw.split("\n").filter((l) => !l.trim().startsWith("```")).join("\n");
                      }
                      planData = JSON.parse(raw);
                    } catch {
                      alert("AI returned invalid plan format. Please try again.");
                      setGenerating(false);
                      return;
                    }
                  }
                  setPlan(planData);
                  setGenerating(false);
                } else if (r.data.status === "failed") {
                  clearInterval(poll);
                  alert(r.data.error || "Plan generation failed");
                  setGenerating(false);
                }
                // else still "generating" — keep polling
              })
              .catch(() => {
                clearInterval(poll);
                alert("Failed to check generation status");
                setGenerating(false);
              });
          }, 3000);
          // Safety timeout: stop polling after 3 min
          setTimeout(() => { clearInterval(poll); setGenerating(false); }, 180000);
        }
      })
      .catch((err) => {
        alert(err?.response?.data?.error || "Generation failed");
        setGenerating(false);
      });
  };

  const handleSave = () => {
    setSaving(true);
    const payload = mode === "keyword"
      ? { keyword, auto_create: true, plan }
      : { keyword_group: groupName, auto_create: true, plan };
    api.post(`/websites/${selectedSite.id}/topic-clusters/generate/`, payload)
      .then((res) => {
        onCreated(res.data.plan);
        onClose();
      })
      .catch((err) => alert(err?.response?.data?.error || "Save failed"))
      .finally(() => setSaving(false));
  };

  const removePlanCluster = (idx) => {
    if (!plan) return;
    const updated = { ...plan, clusters: plan.clusters.filter((_, i) => i !== idx) };
    setPlan(updated);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">Create Topic Cluster</h3>

        {!plan ? (
          <>
            {/* Mode toggle */}
            <div className="flex gap-2 mb-4">
              <button onClick={() => setMode("keyword")}
                className={`px-3 py-1.5 text-xs rounded-lg ${mode === "keyword" ? "bg-copper text-dark" : "bg-dark-50 text-gray-400"}`}>
                From Keyword
              </button>
              <button onClick={() => setMode("group")}
                className={`px-3 py-1.5 text-xs rounded-lg ${mode === "group" ? "bg-copper text-dark" : "bg-dark-50 text-gray-400"}`}>
                From Keyword Group
              </button>
            </div>

            {mode === "keyword" ? (
              <input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)}
                placeholder="Enter a keyword to build a cluster around..."
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
            ) : (
              <select value={groupName} onChange={(e) => setGroupName(e.target.value)}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper/50 focus:outline-none">
                <option value="">Select a keyword group...</option>
                {groups.map((g) => (
                  <option key={g.group} value={g.group}>{g.group} ({g.count} keywords)</option>
                ))}
              </select>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
              <button onClick={handleGenerate}
                disabled={generating || (!keyword && !groupName)}
                className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 disabled:opacity-50">
                {generating ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? "Generating..." : "Generate Plan"}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Plan review */}
            <div className="space-y-4">
              <div className="bg-dark border border-dark-50 rounded-lg p-4">
                <div className="text-xs text-gray-500 uppercase mb-1">Pillar Page</div>
                <div className="text-base font-semibold text-copper">{plan.pillar?.title}</div>
                <div className="text-xs text-gray-400 mt-1">{plan.pillar?.meta_description}</div>
                <div className="text-xs text-gray-600 mt-1 font-mono">/{plan.pillar?.slug}/</div>
              </div>

              <div className="text-xs text-gray-500 uppercase">Cluster Pages ({plan.clusters?.length || 0})</div>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {(plan.clusters || []).map((cp, i) => (
                  <div key={i} className="bg-dark border border-dark-50 rounded-lg p-3 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-200">{cp.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${CONTENT_TYPE_COLORS[cp.content_type] || "bg-gray-800 text-gray-400"}`}>
                          {CONTENT_TYPE_LABELS[cp.content_type] || cp.content_type}
                        </span>
                        <span className="text-xs text-gray-500 font-mono">/{cp.slug}/</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{cp.brief}</div>
                    </div>
                    <button onClick={() => removePlanCluster(i)} className="text-gray-600 hover:text-red-400 p-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <button onClick={() => setPlan(null)} className="px-4 py-2 text-sm text-gray-400 hover:text-white">
                Back
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 disabled:opacity-50">
                  {saving ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
                  {saving ? "Creating..." : "Create Cluster"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Suggest Clusters Modal ──
function SuggestModal({ selectedSite, onClose }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.post(`/websites/${selectedSite.id}/topic-clusters/suggest/`)
      .then((res) => setSuggestions(res.data.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [selectedSite]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">Suggested Topic Clusters</h3>
        <p className="text-sm text-gray-400">Based on your keyword groups, these topics could benefit from a pillar + cluster strategy.</p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-copper" size={20} />
            <span className="ml-2 text-gray-400 text-sm">Analyzing keyword groups...</span>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No keyword groups found that meet the minimum threshold (3+ keywords).
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.map((s) => (
              <div key={s.keyword_group} className="bg-dark border border-dark-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-semibold text-copper capitalize">{s.keyword_group}</span>
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${s.recommendation === "strong" ? "bg-green-900/30 text-green-400" : "bg-amber-900/30 text-amber-400"}`}>
                      {s.recommendation}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    {s.keyword_count} keywords | {s.total_impressions?.toLocaleString()} impr
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {s.top_keywords?.slice(0, 6).map((kw) => (
                    <span key={kw.keyword} className="text-[10px] bg-dark-50 px-1.5 py-0.5 rounded text-gray-400">{kw.keyword}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Content Preview Modal ──
function ContentPreviewModal({ content, title, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>
        <style>{`
          .preview-content { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 16px; line-height: 1.7; color: #d1d5db; max-width: 800px; margin: 0 auto; padding: 0 24px; }
          .preview-content h1 { font-size: 28px; font-weight: 700; color: #f3f4f6; margin: 0 0 16px; line-height: 1.3; }
          .preview-content h2 { font-size: 22px; font-weight: 600; color: #e5e7eb; margin: 32px 0 12px; line-height: 1.3; border-bottom: 1px solid #374151; padding-bottom: 8px; }
          .preview-content h3 { font-size: 18px; font-weight: 600; color: #d1d5db; margin: 24px 0 8px; }
          .preview-content p { margin: 0 0 16px; }
          .preview-content ul, .preview-content ol { margin: 0 0 16px; padding-left: 24px; }
          .preview-content li { margin-bottom: 6px; }
          .preview-content strong { color: #f3f4f6; font-weight: 600; }
          .preview-content em { font-style: italic; color: #9ca3af; }
          .preview-content a { color: #b87333; text-decoration: underline; }
          .preview-content table { width: 100%; border-collapse: collapse; margin: 16px 0; }
          .preview-content th, .preview-content td { border: 1px solid #374151; padding: 8px 12px; text-align: left; }
          .preview-content th { background: #1f2937; color: #e5e7eb; font-weight: 600; }
          .preview-content blockquote { border-left: 3px solid #b87333; margin: 16px 0; padding: 8px 16px; color: #9ca3af; }
        `}</style>
        <div className="preview-content"
          dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    </div>
  );
}

// ── Cluster Detail View ──
function ClusterDetail({ cluster: initialCluster, selectedSite, onBack, onDeleted }) {
  const [cluster, setCluster] = useState(initialCluster);
  const [generatingPillar, setGeneratingPillar] = useState(initialCluster.pillar_status === "generating");
  const [generatingPage, setGeneratingPage] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [buildingLinks, setBuildingLinks] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  const refreshCluster = useCallback(() => {
    return api.get(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/`)
      .then((res) => {
        setCluster(res.data);
        return res.data;
      })
      .catch(() => null);
  }, [selectedSite, cluster.id]);

  // Poll for status updates when generating
  useEffect(() => {
    const isGenerating = generatingPillar || generatingPage || generatingAll;
    if (!isGenerating) return;

    const interval = setInterval(async () => {
      const data = await refreshCluster();
      if (!data) return;

      // Check if pillar generation completed
      if (generatingPillar && data.pillar_status !== "generating") {
        setGeneratingPillar(false);
        if (data.pillar_status === "failed") {
          alert(data.pillar_error || "Pillar content generation failed");
        }
      }

      // Check if single page generation completed
      if (generatingPage) {
        const page = (data.cluster_pages || []).find((p) => p.id === generatingPage);
        if (page && page.status !== "generating") {
          setGeneratingPage(null);
          if (page.status === "failed") {
            alert("Page content generation failed");
          }
        }
      }

      // Check if "generate all" completed (no pages or pillar still generating)
      if (generatingAll) {
        const anyStillGenerating = data.pillar_status === "generating" ||
          (data.cluster_pages || []).some((p) => p.status === "generating");
        if (!anyStillGenerating) {
          setGeneratingAll(false);
          setGeneratingPillar(false);
          const failed = (data.cluster_pages || []).filter((p) => p.status === "failed");
          if (data.pillar_status === "failed" || failed.length > 0) {
            const msgs = [];
            if (data.pillar_status === "failed") msgs.push("Pillar generation failed");
            if (failed.length > 0) msgs.push(`${failed.length} page(s) failed`);
            alert(`Generation complete with errors: ${msgs.join(", ")}`);
          }
        }
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [generatingPillar, generatingPage, generatingAll, refreshCluster]);

  const handleGeneratePillar = () => {
    setGeneratingPillar(true);
    api.post(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/generate-pillar/`)
      .then(() => {
        // 202 accepted — polling will pick up completion
        setCluster((prev) => ({ ...prev, pillar_status: "generating", pillar_error: "" }));
      })
      .catch((err) => {
        setGeneratingPillar(false);
        alert(err?.response?.data?.error || "Failed to start generation");
      });
  };

  const handleGeneratePage = (pageId) => {
    setGeneratingPage(pageId);
    api.post(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/pages/${pageId}/generate/`)
      .then(() => {
        // 202 accepted — polling will pick up completion
        setCluster((prev) => ({
          ...prev,
          cluster_pages: (prev.cluster_pages || []).map((p) =>
            p.id === pageId ? { ...p, status: "generating" } : p
          ),
        }));
      })
      .catch((err) => {
        setGeneratingPage(null);
        alert(err?.response?.data?.error || "Failed to start generation");
      });
  };

  const handleGenerateAll = () => {
    setGeneratingAll(true);
    setGeneratingPillar(true);
    api.post(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/generate-all/`)
      .then((res) => {
        setCluster((prev) => ({
          ...prev,
          pillar_status: res.data.pillar_queued ? "generating" : prev.pillar_status,
          cluster_pages: (prev.cluster_pages || []).map((p) =>
            ["planned", "failed"].includes(p.status) ? { ...p, status: "generating" } : p
          ),
        }));
      })
      .catch((err) => {
        setGeneratingAll(false);
        setGeneratingPillar(false);
        alert(err?.response?.data?.error || "Failed to start generation");
      });
  };

  const handleBuildLinks = () => {
    setBuildingLinks(true);
    api.post(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/build-links/`)
      .then((res) => {
        refreshCluster();
        const msg = res.data.message || `Built ${res.data.links_built} links. Score: ${res.data.cluster_score}`;
        alert(msg);
      })
      .catch((err) => alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to build links"))
      .finally(() => setBuildingLinks(false));
  };

  const [publishingAll, setPublishingAll] = useState(false);

  const handlePublishAll = () => {
    if (!window.confirm("Publish all draft content (pillar + cluster pages) to WordPress?")) return;
    setPublishingAll(true);
    api.post(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/publish/`)
      .then(() => {
        // Poll for completion
        const poll = setInterval(async () => {
          const data = await refreshCluster();
          if (!data) return;
          const stillPublishing = data.pillar_status === "draft" ||
            (data.cluster_pages || []).some((p) => p.status === "draft");
          if (!stillPublishing) {
            clearInterval(poll);
            setPublishingAll(false);
          }
        }, 5000);
        setTimeout(() => { clearInterval(poll); setPublishingAll(false); refreshCluster(); }, 180000);
      })
      .catch((err) => {
        setPublishingAll(false);
        alert(err?.response?.data?.error || "Failed to start publishing");
      });
  };

  const handleUpdateStatus = (newStatus) => {
    api.patch(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/`, { status: newStatus })
      .then(() => refreshCluster())
      .catch(() => {});
  };

  const handleDeletePage = (pageId) => {
    if (!window.confirm("Delete this cluster page?")) return;
    api.delete(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/pages/${pageId}/`)
      .then(() => refreshCluster())
      .catch(() => {});
  };

  const pages = cluster.cluster_pages || [];
  const hasDraftContent = (cluster.pillar_content && cluster.pillar_status === "draft") ||
    pages.some((p) => p.status === "draft" && p.generated_content);
  const hasPlannedPages = pages.some((p) => ["planned", "failed"].includes(p.status)) ||
    (!cluster.pillar_content || cluster.pillar_status === "not_started" || cluster.pillar_status === "failed");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="text-gray-400 hover:text-white">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold">{cluster.name}</h2>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-gray-500 font-mono">{cluster.core_keyword}</span>
            <span className={`px-2 py-0.5 rounded text-[10px] ${STATUS_COLORS[cluster.status]}`}>
              {cluster.status}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={cluster.status}
            onChange={(e) => handleUpdateStatus(e.target.value)}
            className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-copper/50">
            <option value="planning">Planning</option>
            <option value="building">Building</option>
            <option value="published">Published</option>
            <option value="optimizing">Optimizing</option>
          </select>
          <button
            onClick={() => {
              if (!window.confirm(`Delete cluster "${cluster.name}" and all its pages? This cannot be undone.`)) return;
              api.delete(`/websites/${selectedSite.id}/topic-clusters/${cluster.id}/`)
                .then(() => { if (onDeleted) onDeleted(); else onBack(); })
                .catch(() => alert("Failed to delete cluster"));
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-900/20 text-red-400 rounded-lg hover:bg-red-900/30 transition-colors"
          >
            <Trash2 size={12} /> Delete
          </button>
          <ScoreRing score={cluster.cluster_score || 0} size={56} strokeWidth={4} label="Score" />
        </div>
      </div>

      {/* Hub-and-spoke diagram */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Cluster Map</h3>
        <HubSpokeDiagram cluster={cluster} />
        <div className="flex justify-center gap-6 mt-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-green-500 inline-block" /> Both links</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-yellow-500 inline-block" /> One-way</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-gray-600 inline-block border-dashed border-t" /> No links</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-800 border border-green-500 inline-block" /> Published</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-900 border border-amber-500 inline-block" /> Draft</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-800 border border-gray-500 inline-block" /> Planned</span>
        </div>
      </div>

      {/* Pillar Page Section */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-300">Pillar Page</h3>
          <span className={`px-2 py-0.5 rounded text-[10px] ${PILLAR_STATUS_COLORS[cluster.pillar_status]}`}>
            {cluster.pillar_status?.replace("_", " ")}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-xs text-gray-500">Title</div>
            <div className="text-sm text-gray-200">{cluster.pillar_meta_title || "Not set"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Word Count</div>
            <div className="text-sm font-mono">{cluster.pillar_word_count || 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">URL</div>
            {cluster.pillar_page_url ? (
              <a href={cluster.pillar_page_url} target="_blank" rel="noopener noreferrer"
                className="text-sm text-copper hover:underline flex items-center gap-1 truncate">
                {cluster.pillar_page_url} <ExternalLink size={12} className="shrink-0" />
              </a>
            ) : (
              <div className="text-sm text-gray-400">Not published</div>
            )}
          </div>
        </div>
        {cluster.pillar_status === "failed" && cluster.pillar_error && (
          <div className="text-xs text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg px-3 py-2 mb-3">
            {cluster.pillar_error}
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={handleGeneratePillar} disabled={generatingPillar || cluster.pillar_status === "generating"}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-copper text-dark rounded-lg hover:bg-copper/90 disabled:opacity-50">
            {generatingPillar || cluster.pillar_status === "generating"
              ? <Loader size={12} className="animate-spin" />
              : <Wand2 size={12} />}
            {generatingPillar || cluster.pillar_status === "generating" ? "Generating..." : "Generate Content"}
          </button>
          {cluster.pillar_content && (
            <button onClick={() => setPreviewContent({ content: cluster.pillar_content, title: "Pillar Page Content" })}
              className="flex items-center gap-2 px-3 py-1.5 text-xs bg-dark-50 text-gray-300 rounded-lg hover:bg-dark-50/80">
              <Eye size={12} /> Preview
            </button>
          )}
          {cluster.pillar_page_url && (
            <a href={cluster.pillar_page_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 text-xs bg-dark-50 text-gray-300 rounded-lg hover:bg-dark-50/80">
              <ExternalLink size={12} /> View on Site
            </a>
          )}
        </div>
      </div>

      {/* Pipeline actions */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-300">Pipeline</h3>
          <span className="text-[10px] text-gray-500">Generate Content → Build Links → Publish to WP</span>
        </div>
        {generatingAll && (
          <div className="mb-3 text-xs text-gray-400">
            <span className="text-blue-400">Generating:</span>{" "}
            Pillar {cluster.pillar_status === "generating" ? "⏳" : cluster.pillar_status === "draft" ? "✓" : cluster.pillar_status === "failed" ? "✗" : "—"}{" | "}
            Pages: {pages.filter((p) => p.status === "draft" || p.status === "published").length}/{pages.length} done
            {pages.some((p) => p.status === "failed") && `, ${pages.filter((p) => p.status === "failed").length} failed`}
          </div>
        )}
        <div className="flex gap-2">
          {/* Step 1: Generate All Content */}
          <button onClick={handleGenerateAll}
            disabled={generatingAll || generatingPillar || !hasPlannedPages}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-copper text-dark rounded-lg hover:bg-copper/90 disabled:opacity-50">
            {generatingAll ? <Loader size={12} className="animate-spin" /> : <Wand2 size={12} />}
            {generatingAll ? "Generating..." : "1. Generate All Content"}
          </button>

          {/* Step 2: Build All Links */}
          <button onClick={handleBuildLinks}
            disabled={buildingLinks || !hasDraftContent}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-green-900/30 text-green-400 rounded-lg hover:bg-green-900/40 disabled:opacity-50">
            {buildingLinks ? <Loader size={12} className="animate-spin" /> : <Link2 size={12} />}
            {buildingLinks ? "Building..." : "2. Build All Links"}
          </button>

          {/* Step 3: Publish All to WP */}
          <button onClick={handlePublishAll}
            disabled={publishingAll || !hasDraftContent}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-900/30 text-blue-400 rounded-lg hover:bg-blue-900/40 disabled:opacity-50">
            {publishingAll ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
            {publishingAll ? "Publishing..." : "3. Publish All to WP"}
          </button>
        </div>
      </div>

      {/* Cluster Pages header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">Cluster Pages ({pages.length})</h3>
      </div>

      {/* Cluster Pages Table */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-dark-50">
                <th className="text-left py-2.5 px-3 font-medium">#</th>
                <th className="text-left py-2.5 px-3 font-medium">Title</th>
                <th className="text-left py-2.5 px-2 font-medium">Keyword</th>
                <th className="text-left py-2.5 px-2 font-medium">Type</th>
                <th className="text-left py-2.5 px-2 font-medium">Status</th>
                <th className="text-center py-2.5 px-2 font-medium">Links</th>
                <th className="py-2.5 px-2 font-medium text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p, i) => (
                <tr key={p.id} className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors">
                  <td className="py-2 px-3 text-gray-500 font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-3">
                    <div className="text-xs text-gray-200">{p.title}</div>
                    <div className="text-[10px] text-gray-600 font-mono">{p.suggested_slug}</div>
                  </td>
                  <td className="py-2 px-2 text-xs text-gray-400 font-mono">{p.target_keyword}</td>
                  <td className="py-2 px-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${CONTENT_TYPE_COLORS[p.content_type] || "bg-gray-800 text-gray-400"}`}>
                      {CONTENT_TYPE_LABELS[p.content_type] || p.content_type}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      p.status === "published" ? "bg-green-900/30 text-green-400" :
                      p.status === "draft" ? "bg-amber-900/30 text-amber-400" :
                      p.status === "generating" ? "bg-blue-900/30 text-blue-400" :
                      p.status === "failed" ? "bg-red-900/30 text-red-400" :
                      "bg-gray-700 text-gray-400"
                    }`}>
                      {p.status === "generating" ? "generating..." : p.status}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <LinkStatusIcon linkedToPillar={p.linked_to_pillar} pillarLinksHere={p.pillar_links_here} />
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex flex-wrap gap-1">
                      <button onClick={() => handleGeneratePage(p.id)} disabled={generatingPage === p.id}
                        className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors disabled:opacity-50"
                        title="Generate content">
                        {generatingPage === p.id ? <Loader size={10} className="animate-spin" /> : <Wand2 size={10} />}
                        Content
                      </button>
                      {p.generated_content && (
                        <button onClick={() => setPreviewContent({ content: p.generated_content, title: p.title })}
                          className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-blue-900/20 text-blue-400 rounded hover:bg-blue-900/30 transition-colors"
                          title="Preview content">
                          <Eye size={10} />
                        </button>
                      )}
                      {p.page_url && (
                        <a href={p.page_url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-dark-50 text-gray-400 rounded hover:bg-dark-50/80">
                          <ExternalLink size={10} />
                        </a>
                      )}
                      <button onClick={() => handleDeletePage(p.id)}
                        className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-red-900/20 text-red-400 rounded hover:bg-red-900/30 transition-colors"
                        title="Delete page">
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {pages.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500 text-sm">
                    No cluster pages yet. Generate a plan or add pages manually.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {previewContent && (
        <ContentPreviewModal
          content={previewContent.content}
          title={previewContent.title}
          onClose={() => setPreviewContent(null)}
        />
      )}
    </div>
  );
}

// ── Main Topic Clusters Component ──
export default function TopicClusters({ selectedSite }) {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | detail
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);

  // Reset view state when switching websites
  useEffect(() => {
    setView("list");
    setSelectedCluster(null);
    setClusters([]);
  }, [selectedSite]);

  const fetchClusters = useCallback(() => {
    if (!selectedSite) return;
    setLoading(true);
    api.get(`/websites/${selectedSite.id}/topic-clusters/`)
      .then((res) => setClusters(res.data.results || res.data || []))
      .catch(() => setClusters([]))
      .finally(() => setLoading(false));
  }, [selectedSite]);

  useEffect(() => { fetchClusters(); }, [fetchClusters]);

  const openDetail = (clusterId) => {
    api.get(`/websites/${selectedSite.id}/topic-clusters/${clusterId}/`)
      .then((res) => {
        setSelectedCluster(res.data);
        setView("detail");
      })
      .catch(() => alert("Failed to load cluster"));
  };

  const handleCreated = () => {
    fetchClusters();
  };

  const stats = useMemo(() => {
    const total = clusters.length;
    const active = clusters.filter((c) => c.status !== "planning").length;
    const avgScore = total > 0 ? Math.round(clusters.reduce((s, c) => s + (c.cluster_score || 0), 0) / total) : 0;
    const totalPages = clusters.reduce((s, c) => s + (c.total_cluster_pages || 0), 0);
    return { total, active, avgScore, totalPages };
  }, [clusters]);

  if (!selectedSite) {
    return <div className="text-gray-500 text-center py-20">Select a website to view topic clusters.</div>;
  }

  if (view === "detail" && selectedCluster) {
    return (
      <ClusterDetail
        cluster={selectedCluster}
        selectedSite={selectedSite}
        onBack={() => { setView("list"); fetchClusters(); }}
        onDeleted={() => { setView("list"); setSelectedCluster(null); fetchClusters(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Network size={20} className="text-copper" />
          Topic Clusters
        </h2>
        <div className="flex gap-2">
          <button onClick={() => setShowSuggest(true)}
            className="flex items-center gap-2 px-3 py-2 bg-dark-300 border border-dark-50 text-gray-300 rounded-lg text-sm hover:bg-dark-50/50 transition-colors">
            <Sparkles size={14} /> AI Suggest
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 transition-colors">
            <Plus size={14} /> Create Cluster
          </button>
        </div>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Clusters", value: stats.total, color: "text-copper" },
          { label: "Active Clusters", value: stats.active, color: "text-green-400" },
          { label: "Avg Score", value: stats.avgScore, color: "text-amber-400" },
          { label: "Total Pages", value: stats.totalPages, color: "text-blue-400" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-dark-300 border border-dark-50 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-1">{label}</div>
            <div className={`text-2xl font-mono font-bold ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Cluster cards */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader className="animate-spin text-copper" size={20} />
          <span className="ml-2 text-gray-400 text-sm">Loading clusters...</span>
        </div>
      ) : clusters.length === 0 ? (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center">
          <Network size={48} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-gray-300 mb-2">No Topic Clusters Yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Create your first topic cluster to build a pillar SEO strategy.
          </p>
          <button onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90">
            <Plus size={14} /> Create Your First Cluster
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clusters.map((c) => (
            <div key={c.id}
              className="bg-dark-300 border border-dark-50 rounded-xl p-5 hover:border-copper/30 transition-colors cursor-pointer"
              onClick={() => openDetail(c.id)}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-200 truncate">{c.name}</h3>
                  <div className="text-xs text-gray-500 font-mono mt-0.5">{c.core_keyword}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Delete cluster "${c.name}" and all its pages?`)) return;
                      api.delete(`/websites/${selectedSite.id}/topic-clusters/${c.id}/`)
                        .then(() => fetchClusters())
                        .catch(() => alert("Failed to delete cluster"));
                    }}
                    className="text-gray-600 hover:text-red-400 p-1 transition-colors"
                    title="Delete cluster"
                  >
                    <Trash2 size={14} />
                  </button>
                  <ScoreRing score={c.cluster_score || 0} size={48} strokeWidth={4} />
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2 py-0.5 rounded text-[10px] ${STATUS_COLORS[c.status]}`}>
                  {c.status}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] ${PILLAR_STATUS_COLORS[c.pillar_status]}`}>
                  Pillar: {c.pillar_status?.replace("_", " ")}
                </span>
                {c.keyword_group && (
                  <span className="text-[10px] bg-dark-50 px-1.5 py-0.5 rounded text-gray-400">
                    {c.keyword_group}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-xs text-gray-500">Pages</div>
                  <div className="text-sm font-mono">
                    <span className="text-green-400">{c.published_cluster_pages || 0}</span>
                    <span className="text-gray-600">/{c.total_cluster_pages || 0}</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Links</div>
                  <div className="text-sm font-mono text-gray-300">{c.internal_links_built || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Score</div>
                  <div className={`text-sm font-mono font-bold ${
                    (c.cluster_score || 0) >= 70 ? "text-green-400" :
                    (c.cluster_score || 0) >= 40 ? "text-amber-400" : "text-red-400"
                  }`}>
                    {c.cluster_score || 0}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateClusterModal
          selectedSite={selectedSite}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
      {showSuggest && (
        <SuggestModal
          selectedSite={selectedSite}
          onClose={() => setShowSuggest(false)}
        />
      )}
    </div>
  );
}
